/* BarCardStatus — حالة بِطاقة نِقابة المحامين داخل /account.
   - يَعرض حالة التَّوثيق (مَوثَّق / غير مَوثَّق / مُنتَهي).
   - يَفتح BarCardScanner لِإعادة المَسح ثُمّ يُحَدِّث الحساب. */
import { useState } from "react";
import { toast } from "sonner";
import {
  IdentificationCard, SealCheck, WarningCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import BarCardScanner from "../signup/BarCardScanner";
import { attachBarCard } from "../../lib/api";
import { gradeLabel, isVerified } from "../../lib/lawyerVerification";

export default function BarCardStatus({ user, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const verified = isVerified(user);
  const grade = gradeLabel(user?.bar_validity_grade);
  const expiry = (user?.bar_validity_until || "").trim();
  const hasCard = !!(user?.bar_id_number || "").trim();

  const onComplete = async (data) => {
    setOpen(false);
    setSaving(true);
    try {
      const fresh = await attachBarCard({
        bar_id_number: data.bar_id_number,
        bar_barcode: data.bar_barcode,
        validity_grade: data.validity_grade,
        issue_date: data.issue_date,
        validity_until: data.validity_until,
      });
      toast.success("تَمّ تَوثيق الحساب بِبِطاقة النِّقابة");
      onUpdated && onUpdated(fresh);
    } catch (e) {
      const msg = e?.response?.data?.detail || "تَعَذَّر التَّوثيق";
      toast.error(typeof msg === "string" ? msg : "تَعَذَّر التَّوثيق");
    } finally {
      setSaving(false);
    }
  };

  const bgColor = verified ? "#F0FDF4" : (hasCard ? "#FEF3C7" : "#F9FAFB");
  const borderColor = verified ? "#86EFAC" : (hasCard ? "#FCD34D" : "#E5E7EB");

  return (
    <>
      <div
        className="rounded-2xl border-2 p-3.5"
        style={{ background: bgColor, borderColor }}
        data-testid="bar-card-status"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: verified ? "#22C55E" : (hasCard ? "#F59E0B" : "#9CA3AF") }}>
            {verified
              ? <SealCheck size={20} weight="fill" color="#fff" />
              : <IdentificationCard size={20} weight="duotone" color="#fff" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[14px] font-bold text-[#0A192F]">
                {verified ? "حِسابك مُوَثَّق" : (hasCard ? "بِطاقة النِّقابة غير سارية" : "حِسابك غير مُوَثَّق")}
              </span>
              {verified && <SealCheck size={14} weight="fill" className="text-[#1D9BF0]" />}
            </div>
            {verified && (
              <div className="text-[11.5px] text-emerald-800 leading-relaxed">
                {grade ? <>الصَّنف: <b>{grade.short}</b> · {grade.full}</> : "مُوَثَّق بِبِطاقة النِّقابة"}
                {expiry && <span className="opacity-80"> · سارية لِغاية {expiry}</span>}
              </div>
            )}
            {!verified && hasCard && (
              <div className="text-[11.5px] text-amber-900 leading-relaxed flex items-start gap-1">
                <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
                <span>البِطاقة المَسحوبة مُنتَهية الصَّلاحية أو ناقِصة — أَعِد مَسحها لِتَوثيق الحساب.</span>
              </div>
            )}
            {!verified && !hasCard && (
              <div className="text-[11.5px] text-[#5A6470] leading-relaxed">
                وَثِّق حِسابك بِبِطاقة نقابة المحامين لِتَظهَر شارة "مُوَثَّق" بِجانب اسمك ولتَكسِب ثقة العُملاء.
              </div>
            )}
          </div>
        </div>

        <button
          type="button" onClick={() => setOpen(true)} disabled={saving}
          data-testid="bar-card-rescan"
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#0A192F] text-white text-[12.5px] font-bold active:scale-95 transition disabled:opacity-50"
        >
          {hasCard ? <ArrowsClockwise size={13} weight="bold" /> : <IdentificationCard size={13} weight="bold" />}
          {hasCard ? "إعادة مَسح البِطاقة" : "مَسح بِطاقة النِّقابة الآن"}
        </button>
      </div>
      <BarCardScanner open={open} onClose={() => setOpen(false)} onComplete={onComplete} />
    </>
  );
}
